import axios from 'axios';

import { API_BASE_URL, API_LOCAL_URL, API_AUTHORIZATION_KEY } from './config';
import { getUser } from './utils/helpers';

const client = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Authorization-key': API_AUTHORIZATION_KEY,
  },
  timeout: 12000,
});

const apiSettings = {
  fetchBestSeller: async () => {
    const endpoint = '/v1/books/best-sellers';

    try {
      const response = await client.get(endpoint);
      return response.data;
    } catch (error) {
      throw new Error(error.message);
    }
  },
  fetchUserBooks: async () => {
    const savedUser = getUser();
    if (!savedUser) {
      throw new Error('No User Found!');
    }
    const endpoint = `/v1/users/${savedUser.userId}/books`;

    try {
      const response = await client.get(endpoint, {
        headers: {
          'x-access-token': `Bearer ${savedUser.token}`,
        },
      });

      return response.data;
    } catch (error) {
      throw new Error(error.message);
    }
  },
  searchBooks: async (title, author) => {
    const endpoint = `/v1/books/search?title=${title}&author=${author}`;

    if (endpoint) {
      try {
        const response = await client.get(endpoint);
        return response.data;
      } catch (error) {
        throw new Error(error.message);
      }
    } else {
      return { error: 'Provide information to search!' };
    }
  },
  signUp: async (email, password) => {
    const endpoint = '/v1/register/users';
    const bodyData = {
      email,
      password,
    };

    const data = {
      user: {
        userId: '',
        email: '',
        token: '',
      },
      error: null,
    };

    try {
      const response = await client.post(endpoint, bodyData);

      if (response?.status === 201) {
        data.user.userId = response.data.user_id;
        data.user.email = response.data.email;
        data.user.token = response.data.token;
      } else {
        data.error = response?.data?.message;
      }

      return data;
    } catch (error) {
      if (error?.response?.status === 500)
        data.error = error.response.statusText;
      else if (
        error.response.status === 400 ||
        error.response.status === 404 ||
        error.response.status === 409
      )
        data.error = error.response.data?.message;
      else data.error = error.response.data;

      return data;
    }
  },
  login: async (email, password) => {
    const endpoint = '/v1/login/users';
    const bodyData = {
      email,
      password,
    };

    const data = {
      user: {
        userId: '',
        email: '',
        token: '',
      },
      error: null,
    };

    try {
      const response = await client.post(endpoint, bodyData);

      if (response.status === 200) {
        data.user.userId = response.data.user_id;
        data.user.email = response.data.username;
        data.user.token = response.data.token;
      } else {
        data.error = response.data.message;
      }

      return data;
    } catch (error) {
      if (error?.response?.status === 500)
        data.error = error.response.statusText;
      else if (
        error?.response?.status === 400 ||
        error?.response?.status === 404
      )
        data.error = error.response.data?.message;
      else data.error = error?.response?.data;

      return data;
    }
  },
  forgotPassword: async (email) => {
    const endpoint = '/v1/users/forgot-password';
    const bodyData = {
      email,
    };

    const data = {
      url: '',
      error: '',
    };

    try {
      const response = await client.post(endpoint, bodyData);

      if (response.status === 200) {
        data.url = response.data.url;
      } else {
        data.error = response.data.message;
      }

      return data;
    } catch (error) {
      if (error?.response) {
        if (error.response?.status === 500)
          data.error = error.response.statusText;
        else if (error.response.status === 400)
          data.error = error.response?.data.message;
        else data.error = error.response?.data;
      } else {
        data.error = 'An error Occurred !';
      }

      return data;
    }
  },
  resetPassword: async (resetToken, password) => {
    const endpoint = `/v1/users/reset-password/${resetToken}`;
    const bodyData = {
      password,
    };

    const data = {
      success: '',
      error: '',
    };

    try {
      const response = await client.put(endpoint, bodyData);

      if (response.status === 201) {
        data.success = response.data.message;
      } else {
        data.error = response.data.message;
      }

      return data;
    } catch (error) {
      if (error.response?.status === 500)
        data.error = error.response?.statusText;
      else if (error.response?.status === 400)
        data.error = error.response.data?.message;
      else data.error = error.response?.data;

      return data;
    }
  },
  addBookToList: async (book, listName) => {
    const savedUser = getUser();
    if (!savedUser) {
      throw new Error('No User Found!');
    }

    const endpoint = `/v1/users/${savedUser.userId}/books/${listName}`;

    const data = {
      success: '',
      error: '',
    };

    try {
      const response = await client.post(endpoint, book, {
        headers: {
          'x-access-token': `Bearer ${savedUser.token}`,
        },
      });

      if (response.status === 201) {
        data.success = response.data.message;
      } else {
        data.error = response.data.message;
      }

      return data;
    } catch (error) {
      if (error.response?.status === 500)
        data.error = error.response?.statusText;
      else if (error.response?.status === 400)
        data.error = error.response?.data.message;
      else if (error.response.status === 409)
        data.error = error.response?.statusText;
      else data.error = error.response?.data;

      return data;
    }
  },
  deleteBook: async (bookId) => {
    const savedUser = getUser();
    if (!savedUser) {
      throw new Error('No User Found!');
    }

    const endpoint = `/v1/users/${savedUser.userId}/books/${bookId}`;

    const data = {
      success: '',
      error: '',
    };

    try {
      const response = await client.delete(endpoint, {
        headers: {
          'x-access-token': `Bearer ${savedUser.token}`,
        },
      });

      if (response.status === 200) {
        data.success = response.data.message;
      } else {
        data.error = response.data.message;
      }

      return data;
    } catch (error) {
      if (error.response?.status === 500)
        data.error = error.response?.statusText;
      else if (error.response?.status === 400)
        data.error = error.response?.data?.message;
      else if (error.response.status === 409)
        data.error = error.response?.statusText;
      else data.error = error.response?.data;

      return data;
    }
  },
  updateBookCurrentPage: async (bookId, currentPage) => {
    const savedUser = getUser();
    if (!savedUser) {
      throw new Error('No User Found!');
    }

    const endpoint = `/v1/users/${savedUser.userId}/books/${bookId}`;
    console.log(endpoint);

    const data = {
      success: '',
      error: '',
    };

    try {
      const response = await client.patch(
        endpoint,
        { currentPage },
        {
          headers: {
            'x-access-token': `Bearer ${savedUser.token}`,
          },
        }
      );

      if (response.status === 201) {
        data.success = response.data.message;
      } else {
        data.error = response.data.message;
      }

      return data;
    } catch (error) {
      if (error.response?.status === 500)
        data.error = error.response?.statusText;
      else if (error.response?.status === 400)
        data.error = error.response?.data?.message;
      else if (error.response?.status === 409)
        data.error = error.response?.statusText;
      else data.error = error.response?.data;

      return data;
    }
  },
  deleteAccount: async () => {
    const savedUser = getUser();
    if (!savedUser) {
      throw new Error('No User Found!');
    }

    const endpoint = `/v1/users/${savedUser.userId}`;

    const data = {
      success: '',
      error: '',
    };

    try {
      const response = await client.delete(endpoint, {
        headers: {
          'x-access-token': `Bearer ${savedUser.token}`,
        },
      });

      if (response.status === 200) {
        data.success = response.data.message;
      } else {
        data.error = response.data.message;
      }

      return data;
    } catch (error) {
      if (error.response?.status === 500)
        data.error = error.response?.statusText;
      else if (error.response?.status === 400)
        data.error = error.response?.data?.message;
      else if (error.response?.status === 409)
        data.error = error.response?.statusText;
      else data.error = error.response?.data;

      return data;
    }
  },
};

export default apiSettings;
